import { YES_NO, applicantSection, otherAttachments, parcelsField, replySection } from "modules/ebiForms/common";
import { proxyWithoutFeeAttachment, representationWithoutFeeSection } from "modules/ebiForms/definitions/sharedHousing";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A11. Wniosek o wykup mieszkania komunalnego lub dzierżawionej działki gminnej — ustawa z 21.08.1997 r. o gospodarce
 * nieruchomościami: lokal — art. 34 ust. 1 pkt 3, art. 37 ust. 2 pkt 1, art. 68 ust. 1 pkt 7 i ust. 2 (bonifikata i jej
 * zwrot); działka — art. 37 ust. 2 pkt 6 (nieruchomość przyległa) i art. 37 ust. 3 (dzierżawca ≥ 10 lat, zabudowa na
 * podstawie pozwolenia — za zgodą rady). Postępowanie cywilnoprawne (bez decyzji). Brak wzoru ogólnopolskiego; układ:
 * lokal — wzór Zgorzelca (zał. 1 do zarządzenia nr 78/18, https://zgorzelec.bip.info.pl/plik.php?id=211359&wer=1, pobrany
 * 22.10.2026: dane nabywców do aktu, lokal, oświadczenie o innym tytule prawnym, dokumenty przy rozwodzie/zgonie), działka
 * — wzór Brochowa (https://brochow.bip.org.pl/pliki/brochow/wniosek_o_sprzedaz_dzialki_w_trybie_bezprzetargowym.pdf,
 * pobrany 22.10.2026: działka, tryb z art. 37 ust. 2, uzasadnienie, oświadczenie o kosztach). Rodzaj nieruchomości jest
 * wariantem wniosku w BE. Warunki z uchwały rady gminy (minimalny okres najmu, bonifikata) — do potwierdzenia z wydziałem.
 */
const FLAT: EbiCondition = { field: "przedmiot", equals: "lokal" };
const PLOT: EbiCondition = { field: "przedmiot", equals: "dzialka" };
const LEASEHOLDER: EbiCondition = { all: [PLOT, { field: "podstawaNabycia", equals: "dzierzawca" }] };
const ADJACENT: EbiCondition = { all: [PLOT, { field: "podstawaNabycia", equals: "przylegla" }] };
const OTHER_MODE: EbiCondition = { all: [PLOT, { field: "podstawaNabycia", equals: "inna" }] };
const CO_BUYERS: EbiCondition = { field: "wspolnabywcy", equals: "tak" };
const DIVORCED: EbiCondition = { all: [FLAT, { field: "sytuacjaRodzinna", equals: "rozwod" }] };
const WIDOWED: EbiCondition = { all: [FLAT, { field: "sytuacjaRodzinna", equals: "zgon" }] };

const MARITAL_STATUS = [
  { value: "wolny", label: "kawaler / panna" },
  { value: "malzenstwo", label: "żonaty / zamężna" },
  { value: "rozwiedziony", label: "rozwiedziony / rozwiedziona" },
  { value: "wdowiec", label: "wdowiec / wdowa" },
  { value: "separacja", label: "w separacji" },
];

const A11: IEbiFormDefinition = {
  code: "A11",
  version: 1,
  title: "Wniosek o nabycie lokalu mieszkalnego lub dzierżawionej działki z zasobu gminy",
  category: EEbiFormCategory.Property,
  office: EEbiFormOffice.Municipality,
  description: "Wykup mieszkania komunalnego przez najemcę albo nabycie działki gminnej (dzierżawionej lub przyległej) bez przetargu.",
  keywords: ["wykup mieszkania", "sprzedaż lokalu", "bonifikata", "działka gminna", "dzierżawa", "tryb bezprzetargowy"],
  organ: "Wójt / Burmistrz / Prezydent Miasta (przeznaczenie do sprzedaży i bonifikaty — rada gminy)",
  legalBasis:
    "art. 34 ust. 1 pkt 3, art. 37 ust. 2 i 3, art. 67 i art. 68 ustawy z dnia 21 sierpnia 1997 r. o gospodarce nieruchomościami; uchwała rady gminy w sprawie zasad zbywania lokali mieszkalnych i nieruchomości oraz udzielania bonifikat",
  request: "Wnoszę o sprzedaż w trybie bezprzetargowym nieruchomości (lokalu mieszkalnego albo działki) wskazanej we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty skarbowej, także od pełnomocnictwa (art. 2 ust. 1 pkt 1 lit. h ustawy o opłacie skarbowej). Nabywca ponosi koszty wyceny, umowy notarialnej i wpisów w księdze wieczystej.",
  variantField: "przedmiot",
  variants: {
    lokal: { title: "Wniosek o nabycie lokalu mieszkalnego z zasobu gminy przez najemcę" },
    dzialka: { title: "Wniosek o nabycie nieruchomości gminnej w trybie bezprzetargowym" },
  },
  sections: [
    applicantSection(),
    representationWithoutFeeSection(),
    {
      code: "przedmiotSekcja",
      title: "Przedmiot wniosku",
      fields: [
        {
          code: "przedmiot",
          type: "radio",
          label: "Wnoszę o sprzedaż",
          required: true,
          options: [
            { value: "lokal", label: "zajmowanego lokalu mieszkalnego, którego jestem najemcą" },
            { value: "dzialka", label: "działki gminnej (dzierżawionej albo przyległej do mojej nieruchomości)" },
          ],
        },
        {
          code: "infoProcedura",
          type: "info",
          label:
            "Sprzedaż nie jest decyzją administracyjną: gmina przeznacza nieruchomość do sprzedaży, publikuje wykaz, zleca wycenę i ustala cenę, a umowę zawiera się u notariusza. Gmina może odmówić sprzedaży.",
        },
        // lokal (wzór Zgorzelca)
        { code: "lokalAdres", type: "text", label: "Adres budynku — ulica, numer, miejscowość", required: true, visibleWhen: FLAT, maxLength: 300 },
        { code: "lokalNumer", type: "text", label: "Numer lokalu", required: true, visibleWhen: FLAT, width: "half", maxLength: 20 },
        { code: "lokalPietro", type: "text", label: "Piętro", visibleWhen: FLAT, width: "half", maxLength: 20, placeholder: "np. parter, II" },
        {
          code: "infoBonifikata",
          type: "info",
          visibleWhen: FLAT,
          label:
            "Pierwszeństwo w nabyciu przysługuje najemcy lokalu wynajętego na czas nieoznaczony (art. 34 ust. 1 pkt 3 ustawy o gospodarce nieruchomościami). Minimalny okres najmu i wysokość bonifikaty określa uchwała rady gminy; bonifikata podlega zwrotowi, jeśli nabywca zbędzie lokal przed upływem 5 lat od nabycia (art. 68 ust. 2–2b).",
        },
        {
          code: "innyTytul",
          type: "radio",
          label: "Oświadczam, że ja (i osoby nabywające wspólnie ze mną)",
          required: true,
          visibleWhen: FLAT,
          options: [
            {
              value: "nieposiadam",
              label: "nie posiadamy tytułu prawnego do innego lokalu mieszkalnego, budynku mieszkalnego ani nieruchomości wykorzystywanej na cele mieszkaniowe w tej miejscowości",
            },
            {
              value: "posiadam",
              label: "posiadamy tytuł prawny do innego lokalu mieszkalnego, budynku mieszkalnego lub nieruchomości wykorzystywanej na cele mieszkaniowe w tej miejscowości",
            },
          ],
        },
        {
          code: "bonifikataWczesniej",
          type: "radio",
          label: "Czy korzystałaś/korzystałeś (lub osoby nabywające wspólnie z Tobą) z bonifikaty przy nabyciu lokalu od gminy lub Skarbu Państwa?",
          hint: "Odpowiedz, jeśli uchwała rady gminy uzależnia bonifikatę od tego warunku.",
          visibleWhen: FLAT,
          options: YES_NO,
        },
        {
          code: "sytuacjaRodzinna",
          type: "radio",
          label: "Sytuacja rodzinna najemcy",
          required: true,
          visibleWhen: FLAT,
          defaultValue: "brak",
          options: [
            { value: "brak", label: "nie dotyczy" },
            { value: "rozwod", label: "jestem osobą rozwiedzioną" },
            { value: "zgon", label: "mój małżonek (współnajemca) nie żyje" },
          ],
        },
        // działka (wzór Brochowa)
        {
          code: "podstawaNabycia",
          type: "radio",
          label: "Podstawa nabycia bez przetargu",
          required: true,
          visibleWhen: PLOT,
          options: [
            {
              value: "dzierzawca",
              label:
                "dzierżawię działkę na podstawie umowy zawartej na co najmniej 10 lat i zabudowałam/zabudowałem ją na podstawie pozwolenia na budowę (art. 37 ust. 3 — za zgodą rady gminy)",
            },
            { value: "przylegla", label: "nabycie poprawi warunki zagospodarowania mojej nieruchomości przyległej (art. 37 ust. 2 pkt 6)" },
            { value: "inna", label: "inny tryb bezprzetargowy z art. 37 ust. 2 ustawy o gospodarce nieruchomościami" },
          ],
        },
        { code: "dzialkaMiejscowosc", type: "text", label: "Miejscowość, w której położona jest działka", required: true, visibleWhen: PLOT, maxLength: 100 },
        { ...parcelsField({ label: "Działki gminne, których dotyczy wniosek", max: 10 }), visibleWhen: PLOT },
        { code: "czescDzialki", type: "checkbox", label: "Wniosek dotyczy części działki — dołączę mapę z zaznaczonym terenem", visibleWhen: PLOT },
        {
          code: "umowaDzierzawy",
          type: "text",
          label: "Umowa dzierżawy — numer, data zawarcia i okres obowiązywania",
          required: true,
          visibleWhen: LEASEHOLDER,
          maxLength: 300,
        },
        {
          code: "przyleglaDzialka",
          type: "text",
          label: "Moja nieruchomość przyległa — miejscowość, obręb i numer działki",
          required: true,
          visibleWhen: ADJACENT,
          maxLength: 300,
        },
        { code: "przyleglaKw", type: "text", label: "Numer księgi wieczystej nieruchomości przyległej", required: true, visibleWhen: ADJACENT, width: "half", maxLength: 30, placeholder: "np. WA1M/00012345/6" },
        { code: "przyleglaPowierzchnia", type: "number", label: "Powierzchnia nieruchomości przyległej [m²]", visibleWhen: ADJACENT, width: "half", decimals: 2, min: 1 },
        {
          code: "innaPodstawa",
          type: "text",
          label: "Punkt art. 37 ust. 2 ustawy o gospodarce nieruchomościami i okoliczności uzasadniające ten tryb",
          required: true,
          visibleWhen: OTHER_MODE,
          maxLength: 500,
        },
        { code: "uzasadnienie", type: "textarea", label: "Uzasadnienie", required: true, visibleWhen: PLOT, maxLength: 5000 },
        {
          code: "oswiadczenieKoszty",
          type: "declaration",
          label:
            "Oświadczam, że poniosę koszty przygotowania nieruchomości do zbycia, koszty sporządzenia umowy notarialnej i ujawnienia nabywcy w księdze wieczystej.",
          requiredWhen: PLOT,
        },
      ],
    },
    {
      code: "nabywcy",
      title: "Dane nabywców do umowy notarialnej",
      fields: [
        { code: "imionaRodzicow", type: "text", label: "Imiona rodziców wnioskodawcy", required: true, width: "half", maxLength: 200 },
        { code: "stanCywilny", type: "select", label: "Stan cywilny", required: true, width: "half", options: MARITAL_STATUS },
        { code: "obywatelstwo", type: "text", label: "Obywatelstwo", required: true, width: "half", maxLength: 100, defaultValue: "polskie" },
        {
          code: "wspolnabywcy",
          type: "radio",
          label: "Czy nieruchomość nabywasz wspólnie z innymi osobami (współnajemcą, współdzierżawcą, małżonkiem)?",
          required: true,
          options: YES_NO,
        },
        {
          code: "wspolnabywcyLista",
          type: "repeat",
          label: "Pozostali nabywcy",
          itemLabel: "Nabywca",
          required: true,
          visibleWhen: CO_BUYERS,
          min: 1,
          max: 10,
          fields: [
            { code: "imieNazwisko", type: "text", label: "Imię i nazwisko", required: true, width: "half", maxLength: 200 },
            { code: "pesel", type: "pesel", label: "PESEL", required: true, width: "half" },
            { code: "imionaRodzicow", type: "text", label: "Imiona rodziców", required: true, width: "half", maxLength: 200 },
            { code: "stanCywilny", type: "select", label: "Stan cywilny", required: true, width: "half", options: MARITAL_STATUS },
            { code: "obywatelstwo", type: "text", label: "Obywatelstwo", required: true, width: "half", maxLength: 100, defaultValue: "polskie" },
            { code: "relacja", type: "text", label: "Kim jest dla wnioskodawcy", width: "half", maxLength: 100, placeholder: "np. małżonek, współnajemca" },
            { code: "adres", type: "text", label: "Adres zamieszkania", required: true, maxLength: 300 },
          ],
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "podpisyWspolnabywcow",
      label: "Wniosek albo oświadczenie o przystąpieniu do wniosku podpisane przez pozostałych nabywców",
      hint: "Nabycie wymaga zgody wszystkich najemców (dzierżawców) i ich małżonków.",
      visibleWhen: CO_BUYERS,
      required: true,
    },
    {
      code: "umowaNajmu",
      label: "Umowa najmu lokalu zawarta na czas nieoznaczony albo decyzja o przydziale lokalu",
      visibleWhen: FLAT,
      required: true,
    },
    {
      code: "brakZaleglosci",
      label: "Potwierdzenie zarządcy o danych lokalu i braku zaległości w opłatach",
      hint: "Nieobowiązkowo — wiele urzędów prosi o potwierdzenie administracji budynku.",
      visibleWhen: FLAT,
    },
    {
      code: "wyrokRozwodowy",
      label: "Wyrok o przyznaniu, podziale lub ustaleniu sposobu korzystania z mieszkania",
      visibleWhen: DIVORCED,
      required: true,
    },
    { code: "aktZgonu", label: "Odpis aktu zgonu małżonka (współnajemcy)", visibleWhen: WIDOWED, required: true },
    { code: "umowaDzierzawyKopia", label: "Umowa dzierżawy zawarta na co najmniej 10 lat", visibleWhen: LEASEHOLDER, required: true },
    {
      code: "pozwolenieNaBudowe",
      label: "Pozwolenie na budowę obiektu wzniesionego na dzierżawionej działce",
      visibleWhen: LEASEHOLDER,
      required: true,
    },
    {
      code: "mapa",
      label: "Mapa z oznaczeniem działki (części działki), której dotyczy wniosek",
      visibleWhen: { any: [LEASEHOLDER, { all: [PLOT, { field: "czescDzialki", equals: true }] }] },
      required: true,
    },
    { code: "odpisKw", label: "Odpis księgi wieczystej nieruchomości przyległej", visibleWhen: ADJACENT, required: true },
    proxyWithoutFeeAttachment(
      "Pełnomocnictwo w sprawie z ustawy o gospodarce nieruchomościami jest zwolnione z opłaty skarbowej. Do zawarcia umowy u notariusza potrzebne jest pełnomocnictwo w formie aktu notarialnego.",
    ),
    otherAttachments(),
  ],
};

export default A11;
